import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { applyChanges, dataOnly, EMPTY_DATA, sameData } from "@/lib/listSync";

const read = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } };

export function useAccountData(user, authChecked) {
  // Keep a tab's cached data and its matching base together. Reading the base
  // again after auth resolves can pick up another tab's newer snapshot.
  const [initial] = useState(() => ({
    data: { favorites: read("vv_favs", []), lists: read("vv_lists", []) },
    base: read("vv_base", EMPTY_DATA), owner: localStorage.getItem("vv_owner"),
  }));
  const [data, setData] = useState(initial.data);
  const [status, setStatus] = useState("local");
  const [retry, setRetry] = useState(0);
  const current = useRef(data);
  const base = useRef(initial.base);
  const owner = useRef(initial.owner);
  const generation = useRef(0);
  const ready = useRef(false);
  const pending = useRef(null);
  current.current = data;

  const update = useCallback((next) => {
    const value = typeof next === "function" ? next(current.current) : next;
    current.current = value; setData(value);
  }, []);

  const clearPrivate = useCallback(() => {
    generation.current++;
    ready.current = false; pending.current = null; owner.current = null; base.current = EMPTY_DATA;
    ["vv_owner", "vv_base", "vv_favs", "vv_lists"].forEach((key) => localStorage.removeItem(key));
    update(EMPTY_DATA); setStatus("local");
  }, [update]);

  const persist = useCallback((value, account, savedBase) => {
    localStorage.setItem("vv_favs", JSON.stringify(value.favorites));
    localStorage.setItem("vv_lists", JSON.stringify(value.lists));
    if (account) { localStorage.setItem("vv_owner", account); localStorage.setItem("vv_base", JSON.stringify(savedBase)); }
    else { localStorage.removeItem("vv_owner"); localStorage.removeItem("vv_base"); }
  }, []);

  const flush = useCallback(async () => {
    if (pending.current) return pending.current;
    if (!user) return;
    if (!ready.current) throw new Error("Sync not ready");
    const epoch = generation.current;
    const promise = (async () => {
      try {
        while (epoch === generation.current && !sameData(current.current, base.current)) {
          setStatus("saving");
          const sent = current.current;
          const { data: result } = await api.put("/me/data", { ...sent, base: base.current });
          if (epoch !== generation.current) return;
          const merged = applyChanges(dataOnly(result), sent, current.current);
          base.current = dataOnly(result);
          update(merged); persist(merged, user.user_id, base.current);
        }
        if (epoch === generation.current) setStatus("synced");
      } catch (error) { if (epoch === generation.current) setStatus("error"); throw error; }
    })();
    pending.current = promise;
    try { await promise; } finally { if (pending.current === promise) pending.current = null; }
  }, [user, update, persist]);

  useEffect(() => {
    if (!authChecked) return;
    const epoch = ++generation.current;
    ready.current = false; pending.current = null;
    if (!user) {
      if (owner.current) { update(EMPTY_DATA); owner.current = null; persist(EMPTY_DATA, null, EMPTY_DATA); }
      setStatus("local"); return;
    }
    const account = user.user_id;
    if (owner.current && owner.current !== account) update(EMPTY_DATA);
    const localBase = owner.current === account ? base.current : EMPTY_DATA;
    base.current = localBase;
    owner.current = account;
    setStatus("saving");
    (async () => {
      try {
        const { data: remote } = await api.get("/me/data");
        if (epoch !== generation.current) return;
        const before = current.current;
        const merged = applyChanges(dataOnly(remote), localBase, before);
        base.current = dataOnly(remote);
        update(merged);
        const { data: result } = await api.put("/me/data", { ...merged, base: dataOnly(remote) });
        if (epoch !== generation.current) return;
        const latest = applyChanges(dataOnly(result), merged, current.current);
        base.current = dataOnly(result); ready.current = true;
        update(latest); persist(latest, account, base.current);
        setStatus(sameData(latest, base.current) ? "synced" : "saving");
      } catch { if (epoch === generation.current) setStatus("error"); }
    })();
    return () => { if (generation.current === epoch) generation.current = epoch + 1; ready.current = false; };
    // Account changes, not individual edits, trigger the initial reconciliation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, authChecked, retry]);

  useEffect(() => {
    if (!authChecked) return;
    persist(data, owner.current, base.current);
    if (!ready.current || !user || sameData(data, base.current)) return;
    setStatus("saving");
    const timer = setTimeout(() => flush().catch(() => {}), 250);
    return () => clearTimeout(timer);
  }, [data, user, authChecked, persist, flush]);

  useEffect(() => {
    const refresh = async () => {
      if (!user || !ready.current || pending.current || !sameData(current.current, base.current)) return;
      const epoch = generation.current;
      const before = current.current;
      try {
        const { data: result } = await api.get("/me/data");
        if (epoch !== generation.current || pending.current || !sameData(before, current.current)) return;
        base.current = dataOnly(result); update(base.current); persist(base.current, user.user_id, base.current);
      } catch { setStatus("error"); }
    };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [user, update, persist]);

  return { data, update, status, flush, clearPrivate, retrySync: () => setRetry((n) => n + 1) };
}